import { ChartOptions } from 'chart.js';
import {
  ALFA_MAPA_CALOR_SUPER_EJECUTIVO,
  ALTO_GRAFICA_SUPER_EJECUTIVO,
  COLOR_BLANCO_GRAFICA_SUPER_EJECUTIVO,
  COLOR_TEXTO_GRAFICA_SUPER_EJECUTIVO,
  LIMITES_GRAFICA_SUPER_EJECUTIVO,
  MAXIMO_BARRAS_VISIBLES_SUPER_EJECUTIVO,
  MAXIMO_CATEGORIAS_DONA_VISIBLES_SUPER_EJECUTIVO,
  MAXIMO_PUNTOS_CON_CIFRA_SUPER_EJECUTIVO,
  MENSAJES_GRAFICA_SUPER_EJECUTIVO,
  MINIMO_CATEGORIAS_EN_OTROS_SUPER_EJECUTIVO,
  PALETA_GRAFICA_SUPER_EJECUTIVO,
  RGB_MAPA_CALOR_SUPER_EJECUTIVO,
} from '../super-ejecutivo.constants';
import {
  IConfiguracionChartSuperEjecutivo,
  IGraficaCategoriasSuperEjecutivo,
  IGraficaDispersionSuperEjecutivo,
  IGraficaSuperEjecutivo,
  SemaforoGraficaSuperEjecutivo,
  TipoGraficaSuperEjecutivo,
} from '../super-ejecutivo.interface';

/**
 * Validación y preparación de las gráficas del Super Ejecutivo (requerimiento `super-ejecutivo-graficas`).
 * El contrato es el mismo que valida el Super Ejecutivo (`EspecificacionGrafica`): aquí se comprueba a mano,
 * porque Kobra Admin no tiene zod, y se deja la gráfica lista para dibujarse (orden, "Otros", colores).
 */

const TIPOS: readonly TipoGraficaSuperEjecutivo[] = [
  'dona',
  'barras',
  'linea',
  'area',
  'barrasApiladas',
  'barrasAgrupadas',
  'mapaCalor',
  'progreso',
  'cifras',
  'dispersion',
];

const TIPOS_CON_CATEGORIAS: readonly TipoGraficaSuperEjecutivo[] = ['dona', 'barras', 'linea', 'area', 'barrasApiladas', 'barrasAgrupadas'];

/** Gráficas de barras: las únicas que se ordenan de mayor a menor y aceptan `orden` (RN2). */
const esDeBarras = (tipo: TipoGraficaSuperEjecutivo) => tipo === 'barras' || tipo === 'barrasApiladas' || tipo === 'barrasAgrupadas';

/** Gráficas que se dibujan en un lienzo de chart.js; las demás son HTML. */
const TIPOS_DE_LIENZO: readonly TipoGraficaSuperEjecutivo[] = [...TIPOS_CON_CATEGORIAS, 'dispersion'];

const LIMITES = LIMITES_GRAFICA_SUPER_EJECUTIVO;

type Rango = { min: number; max: number };

/** Un error de validación del contrato, con el campo que falló. */
class GraficaInvalidaError extends Error {}

const esObjeto = (valor: unknown): valor is Record<string, unknown> => typeof valor === 'object' && valor !== null && !Array.isArray(valor);

function texto(valor: unknown, campo: string, largo: number = LIMITES.largoTexto): string {
  if (typeof valor !== 'string' || valor.trim() === '' || valor.length > largo) {
    throw new GraficaInvalidaError(`${campo} debe ser un texto de 1 a ${largo} caracteres.`);
  }

  return valor;
}

function textoOpcional(valor: unknown, campo: string, largo: number = LIMITES.largoTexto): string | undefined {
  return valor === undefined ? undefined : texto(valor, campo, largo);
}

function numero(valor: unknown, campo: string): number {
  if (typeof valor !== 'number' || !Number.isFinite(valor)) {
    throw new GraficaInvalidaError(`${campo} debe ser un número.`);
  }

  return valor;
}

function lista(valor: unknown, campo: string, rango: Rango): unknown[] {
  if (!Array.isArray(valor) || valor.length < rango.min || valor.length > rango.max) {
    throw new GraficaInvalidaError(`${campo} debe tener de ${rango.min} a ${rango.max} elementos.`);
  }

  return valor;
}

const textos = (valor: unknown, campo: string, rango: Rango) => lista(valor, campo, rango).map((v, i) => texto(v, `${campo}[${i}]`));

function base(valor: Record<string, unknown>) {
  return {
    titulo: texto(valor['titulo'], 'titulo'),
    unidad: textoOpcional(valor['unidad'], 'unidad'),
    nota: textoOpcional(valor['nota'], 'nota', LIMITES.largoNota),
  };
}

function conCategorias(valor: Record<string, unknown>, tipo: IGraficaCategoriasSuperEjecutivo['tipo']): IGraficaCategoriasSuperEjecutivo {
  const rangos: Record<IGraficaCategoriasSuperEjecutivo['tipo'], { categorias: Rango; series: Rango }> = {
    dona: { categorias: LIMITES.categoriasDona, series: { min: 1, max: 1 } },
    barras: { categorias: LIMITES.categoriasBarras, series: LIMITES.seriesBarras },
    linea: { categorias: LIMITES.categoriasLinea, series: LIMITES.seriesLinea },
    area: { categorias: LIMITES.categoriasLinea, series: LIMITES.seriesLinea },
    barrasApiladas: { categorias: LIMITES.categoriasBarras, series: LIMITES.seriesApiladas },
    barrasAgrupadas: { categorias: LIMITES.categoriasBarras, series: LIMITES.seriesApiladas },
  };
  const categorias = textos(valor['categorias'], 'categorias', rangos[tipo].categorias);
  const series = lista(valor['series'], 'series', rangos[tipo].series).map((s, i) => {
    if (!esObjeto(s)) {
      throw new GraficaInvalidaError(`series[${i}] debe ser un objeto.`);
    }

    const nombre = texto(s['nombre'], `series[${i}].nombre`);
    const valores = lista(s['valores'], `series[${i}].valores`, { min: 1, max: Number.MAX_SAFE_INTEGER }).map((v, j) =>
      numero(v, `series[${i}].valores[${j}]`),
    );

    if (valores.length !== categorias.length) {
      throw new GraficaInvalidaError(`La serie "${nombre}" tiene ${valores.length} valores y hay ${categorias.length} categorías.`);
    }

    return { nombre, valores };
  });
  const orden = valor['orden'] as IGraficaCategoriasSuperEjecutivo['orden'];

  if (orden !== undefined && orden !== 'mayorAMenor' && orden !== 'original') {
    throw new GraficaInvalidaError('orden debe ser "mayorAMenor" u "original".');
  }

  if (orden !== undefined && !esDeBarras(tipo)) {
    throw new GraficaInvalidaError('orden solo aplica a barras, barrasApiladas y barrasAgrupadas.');
  }

  return { tipo, ...base(valor), categorias, series, ...(orden === undefined ? {} : { orden }) };
}

function validar(valor: unknown): IGraficaSuperEjecutivo {
  if (!esObjeto(valor)) {
    throw new GraficaInvalidaError('La gráfica debe ser un objeto JSON.');
  }

  const tipo = valor['tipo'];

  if (typeof tipo !== 'string' || !TIPOS.includes(tipo as TipoGraficaSuperEjecutivo)) {
    throw new GraficaInvalidaError(`tipo debe ser uno de: ${TIPOS.join(', ')}.`);
  }

  if (TIPOS_CON_CATEGORIAS.includes(tipo as TipoGraficaSuperEjecutivo)) {
    return conCategorias(valor, tipo as IGraficaCategoriasSuperEjecutivo['tipo']);
  }

  switch (tipo) {
    case 'mapaCalor': {
      const filas = textos(valor['filas'], 'filas', LIMITES.filasMapaCalor);
      const columnas = textos(valor['columnas'], 'columnas', LIMITES.columnasMapaCalor);
      const valores = lista(valor['valores'], 'valores', { min: filas.length, max: filas.length }).map((fila, i) => {
        const celdas = lista(fila, `valores[${i}]`, { min: columnas.length, max: columnas.length });

        return celdas.map((v, j) => numero(v, `valores[${i}][${j}]`));
      });

      return { tipo, ...base(valor), filas, columnas, valores };
    }
    case 'progreso': {
      const total = numero(valor['total'], 'total');
      const valorProgreso = numero(valor['valor'], 'valor');
      const semaforo = valor['semaforo'] as SemaforoGraficaSuperEjecutivo | undefined;

      if (total <= 0) {
        throw new GraficaInvalidaError('total debe ser mayor que 0.');
      }

      if (valorProgreso < 0) {
        throw new GraficaInvalidaError('valor debe ser mayor o igual que 0.');
      }

      if (semaforo !== undefined && semaforo !== 'verde' && semaforo !== 'naranja' && semaforo !== 'rojo') {
        throw new GraficaInvalidaError('semaforo debe ser "verde", "naranja" o "rojo".');
      }

      return {
        tipo,
        ...base(valor),
        valor: valorProgreso,
        total,
        ...(semaforo === undefined ? {} : { semaforo }),
      };
    }
    case 'cifras': {
      const cifras = lista(valor['cifras'], 'cifras', LIMITES.cifras).map((c, i) => {
        if (!esObjeto(c)) {
          throw new GraficaInvalidaError(`cifras[${i}] debe ser un objeto.`);
        }

        return {
          etiqueta: texto(c['etiqueta'], `cifras[${i}].etiqueta`),
          valor: numero(c['valor'], `cifras[${i}].valor`),
          unidad: textoOpcional(c['unidad'], `cifras[${i}].unidad`),
          detalle: textoOpcional(c['detalle'], `cifras[${i}].detalle`),
        };
      });

      return { tipo, ...base(valor), cifras };
    }
    default: {
      const puntos = lista(valor['puntos'], 'puntos', LIMITES.puntosDispersion).map((p, i) => {
        if (!esObjeto(p)) {
          throw new GraficaInvalidaError(`puntos[${i}] debe ser un objeto.`);
        }

        return {
          etiqueta: texto(p['etiqueta'], `puntos[${i}].etiqueta`),
          x: numero(p['x'], `puntos[${i}].x`),
          y: numero(p['y'], `puntos[${i}].y`),
        };
      });

      return { tipo: 'dispersion', ...base(valor), ejeX: texto(valor['ejeX'], 'ejeX'), ejeY: texto(valor['ejeY'], 'ejeY'), puntos };
    }
  }
}

/** La gráfica del bloque, validada contra el contrato, o el motivo por el que no se puede mostrar (RN8.1). */
export function interpretarGraficaSuperEjecutivo(json: string): { grafica: IGraficaSuperEjecutivo } | { motivo: string } {
  let valor: unknown;

  try {
    valor = JSON.parse(json);
  } catch {
    return { motivo: 'No es un JSON válido.' };
  }

  try {
    return { grafica: validar(valor) };
  } catch (error) {
    if (error instanceof GraficaInvalidaError) {
      return { motivo: error.message };
    }

    throw error;
  }
}

/** Cifra como la lee el usuario: separador de miles y hasta un decimal. */
const FORMATO_CIFRA = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 1 });

export function formatearCifraSuperEjecutivo(valor: number, unidad?: string): string {
  const cifra = FORMATO_CIFRA.format(valor);

  if (!unidad) {
    return cifra;
  }

  return unidad === '%' ? `${cifra} %` : `${cifra} ${unidad}`;
}

/** Etiqueta del grupo que suma las categorías que no se muestran. */
const otros = (cantidad: number) => MENSAJES_GRAFICA_SUPER_EJECUTIVO.OTROS.replace('{n}', String(cantidad));

/** Índices de las categorías de mayor a menor según `peso`. */
const indicesDeMayorAMenor = (categorias: readonly string[], peso: (i: number) => number) =>
  categorias.map((_, i) => i).sort((a, b) => peso(b) - peso(a));

/** La gráfica con sus categorías (y los valores de cada serie) en el orden de `indices`. */
function reordenar(grafica: IGraficaCategoriasSuperEjecutivo, indices: readonly number[]): IGraficaCategoriasSuperEjecutivo {
  return {
    ...grafica,
    categorias: indices.map((i) => grafica.categorias[i]),
    series: grafica.series.map((s) => ({ ...s, valores: indices.map((i) => s.valores[i]) })),
  };
}

/**
 * Barras de mayor a menor (RN2), salvo `orden: "original"`: las apiladas por el total de la categoría
 * (la suma de sus series), las demás por la primera serie.
 */
function ordenar(grafica: IGraficaCategoriasSuperEjecutivo): IGraficaCategoriasSuperEjecutivo {
  if (!esDeBarras(grafica.tipo) || grafica.orden === 'original') {
    return grafica;
  }

  const peso =
    grafica.tipo === 'barrasApiladas'
      ? (i: number) => grafica.series.reduce((suma, s) => suma + s.valores[i], 0)
      : (i: number) => grafica.series[0].valores[i];

  return reordenar(grafica, indicesDeMayorAMenor(grafica.categorias, peso));
}

/**
 * Caso límite "demasiadas categorías": con una sola serie, las mayores se muestran (aunque la gráfica no
 * venga ordenada, como la dona) y las demás se suman en "Otros (N)"; con `orden: "original"` (rangos) se
 * conserva el orden y "Otros" suma la cola. Con varias series no se suman (podrían ser promedios): se
 * muestran las primeras y la nota dice cuántas faltan.
 */
function recortar(grafica: IGraficaCategoriasSuperEjecutivo, maximo: number): IGraficaCategoriasSuperEjecutivo {
  if (grafica.categorias.length - maximo < MINIMO_CATEGORIAS_EN_OTROS_SUPER_EJECUTIVO) {
    return grafica;
  }

  if (grafica.series.length > 1) {
    return {
      ...grafica,
      categorias: grafica.categorias.slice(0, maximo),
      series: grafica.series.map((s) => ({ ...s, valores: s.valores.slice(0, maximo) })),
      nota: [grafica.nota, `Se muestran ${maximo} de ${grafica.categorias.length}.`].filter(Boolean).join(' '),
    };
  }

  const [serie] = grafica.series;
  const indices =
    grafica.orden === 'original' ? grafica.categorias.map((_, i) => i) : indicesDeMayorAMenor(grafica.categorias, (i) => serie.valores[i]);
  const mayores = indices.slice(0, maximo);
  const sumaDeOtros = indices.slice(maximo).reduce((suma, i) => suma + serie.valores[i], 0);

  return {
    ...grafica,
    categorias: [...mayores.map((i) => grafica.categorias[i]), otros(indices.length - maximo)],
    series: [{ ...serie, valores: [...mayores.map((i) => serie.valores[i]), sumaDeOtros] }],
  };
}

/** La gráfica como se dibuja: ordenada y con "Otros" si hace falta. */
export function prepararGraficaSuperEjecutivo(grafica: IGraficaSuperEjecutivo): IGraficaSuperEjecutivo {
  if (!TIPOS_CON_CATEGORIAS.includes(grafica.tipo)) {
    return grafica;
  }

  const ordenada = ordenar(grafica as IGraficaCategoriasSuperEjecutivo);

  switch (ordenada.tipo) {
    case 'dona':
      return recortar(ordenada, MAXIMO_CATEGORIAS_DONA_VISIBLES_SUPER_EJECUTIVO);
    case 'barras':
    case 'barrasApiladas':
    case 'barrasAgrupadas':
      return recortar(ordenada, MAXIMO_BARRAS_VISIBLES_SUPER_EJECUTIVO);
    default:
      return ordenada;
  }
}

export function esGraficaDeLienzoSuperEjecutivo(grafica: IGraficaSuperEjecutivo): grafica is IGraficaCategoriasSuperEjecutivo | IGraficaDispersionSuperEjecutivo {
  return TIPOS_DE_LIENZO.includes(grafica.tipo);
}

/** Alto del lienzo en px: las barras crecen con sus categorías para que cada etiqueta se lea. */
export function altoGraficaSuperEjecutivo(grafica: IGraficaSuperEjecutivo): number {
  switch (grafica.tipo) {
    case 'dona':
      return ALTO_GRAFICA_SUPER_EJECUTIVO.dona;
    case 'barras':
    case 'barrasApiladas':
    case 'barrasAgrupadas':
      return Math.max(
        ALTO_GRAFICA_SUPER_EJECUTIVO.minimoBarras,
        grafica.categorias.length * ALTO_GRAFICA_SUPER_EJECUTIVO.barraPorCategoria + ALTO_GRAFICA_SUPER_EJECUTIVO.margenBarras,
      );
    case 'dispersion':
      return ALTO_GRAFICA_SUPER_EJECUTIVO.dispersion;
    default:
      return ALTO_GRAFICA_SUPER_EJECUTIVO.linea;
  }
}

const color = (indice: number) => PALETA_GRAFICA_SUPER_EJECUTIVO[indice % PALETA_GRAFICA_SUPER_EJECUTIVO.length];

/** Color del mapa de calor según la intensidad del valor respecto al mayor de la gráfica. */
export function colorIntensidadSuperEjecutivo(valor: number, maximo: number): string {
  const intensidad = maximo > 0 ? Math.max(0, Math.min(1, valor / maximo)) : 0;

  return `rgba(${RGB_MAPA_CALOR_SUPER_EJECUTIVO}, ${(ALFA_MAPA_CALOR_SUPER_EJECUTIVO.minima + intensidad * ALFA_MAPA_CALOR_SUPER_EJECUTIVO.rango).toFixed(2)})`;
}

export function configuracionChartSuperEjecutivo(
  grafica: IGraficaCategoriasSuperEjecutivo | IGraficaDispersionSuperEjecutivo,
): IConfiguracionChartSuperEjecutivo {
  const cifra = (valor: number) => formatearCifraSuperEjecutivo(valor);
  const comunes: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
  };

  if (grafica.tipo === 'dispersion') {
    // RN8: cada punto con su nombre y sus dos cifras mientras caben; con más puntos, solo el nombre y las
    // cifras en el tooltip y los ejes.
    const conCifras = grafica.puntos.length <= MAXIMO_PUNTOS_CON_CIFRA_SUPER_EJECUTIVO;
    const etiqueta = (indice: number) => {
      const punto = grafica.puntos[indice];

      if (!punto) {
        return '';
      }

      return conCifras ? `${punto.etiqueta} (${cifra(punto.x)}, ${cifra(punto.y)})` : punto.etiqueta;
    };

    return {
      type: 'scatter',
      data: {
        datasets: [
          {
            data: grafica.puntos.map((p) => ({ x: p.x, y: p.y })),
            backgroundColor: color(0),
            pointRadius: 6,
            pointHoverRadius: 8,
          },
        ],
      },
      options: {
        ...comunes,
        scales: {
          x: { title: { display: true, text: grafica.ejeX }, ticks: { color: COLOR_TEXTO_GRAFICA_SUPER_EJECUTIVO } },
          y: { title: { display: true, text: grafica.ejeY }, ticks: { color: COLOR_TEXTO_GRAFICA_SUPER_EJECUTIVO } },
        },
        plugins: {
          ...comunes.plugins,
          tooltip: {
            callbacks: {
              label: (item) => {
                const punto = grafica.puntos[item.dataIndex];

                return `${punto.etiqueta}: ${cifra(punto.x)}, ${cifra(punto.y)}`;
              },
            },
          },
          datalabels: { align: 'top', anchor: 'end', color: COLOR_TEXTO_GRAFICA_SUPER_EJECUTIVO, font: { size: 11 }, formatter: (_v, ctx) => etiqueta(ctx.dataIndex) },
        } as ChartOptions['plugins'],
      },
    };
  }

  const variasSeries = grafica.series.length > 1;
  const leyenda = { display: variasSeries, position: 'bottom' as const, labels: { boxWidth: 12, color: COLOR_TEXTO_GRAFICA_SUPER_EJECUTIVO } };

  if (grafica.tipo === 'dona') {
    const [serie] = grafica.series;

    return {
      type: 'doughnut',
      data: {
        // RN8: la leyenda lleva la cifra de cada parte.
        labels: grafica.categorias.map((c, i) => `${c}: ${cifra(serie.valores[i])}`),
        datasets: [
          {
            data: serie.valores,
            backgroundColor: grafica.categorias.map((_, i) => color(i)),
            borderWidth: 2,
            borderColor: COLOR_BLANCO_GRAFICA_SUPER_EJECUTIVO,
          },
        ],
      },
      options: {
        ...comunes,
        cutout: '55%',
        plugins: {
          ...comunes.plugins,
          legend: { display: true, position: 'right', labels: { boxWidth: 12, color: COLOR_TEXTO_GRAFICA_SUPER_EJECUTIVO } },
          datalabels: { display: false },
        } as ChartOptions['plugins'],
      } as ChartOptions,
    };
  }

  if (grafica.tipo === 'linea' || grafica.tipo === 'area') {
    const conCifras = grafica.categorias.length <= MAXIMO_PUNTOS_CON_CIFRA_SUPER_EJECUTIVO;

    return {
      type: 'line',
      data: {
        labels: grafica.categorias,
        datasets: grafica.series.map((s, i) => ({
          label: s.nombre,
          data: s.valores,
          borderColor: color(i),
          backgroundColor: grafica.tipo === 'area' ? `${color(i)}33` : color(i),
          fill: grafica.tipo === 'area',
          tension: 0.3,
          pointRadius: 3,
        })),
      },
      options: {
        ...comunes,
        scales: {
          x: { ticks: { color: COLOR_TEXTO_GRAFICA_SUPER_EJECUTIVO, maxRotation: 45, autoSkip: true } },
          y: { beginAtZero: true, ticks: { color: COLOR_TEXTO_GRAFICA_SUPER_EJECUTIVO } },
        },
        plugins: {
          ...comunes.plugins,
          legend: leyenda,
          // RN8: cada valor con su cifra mientras caben; después, en el tooltip.
          datalabels: { display: conCifras, align: 'top', anchor: 'end', color: COLOR_TEXTO_GRAFICA_SUPER_EJECUTIVO, font: { size: 10 }, formatter: (v: number) => cifra(v) },
        } as ChartOptions['plugins'],
      },
    };
  }

  const apiladas = grafica.tipo === 'barrasApiladas';

  return {
    type: 'bar',
    data: {
      labels: grafica.categorias,
      datasets: grafica.series.map((s, i) => ({
        label: s.nombre,
        data: s.valores,
        backgroundColor: color(i),
        borderRadius: 3,
        maxBarThickness: 22,
      })),
    },
    options: {
      ...comunes,
      indexAxis: 'y',
      layout: { padding: { right: apiladas ? 8 : 48 } },
      scales: {
        x: { beginAtZero: true, stacked: apiladas, ticks: { color: COLOR_TEXTO_GRAFICA_SUPER_EJECUTIVO, precision: 0 } },
        y: { stacked: apiladas, ticks: { color: COLOR_TEXTO_GRAFICA_SUPER_EJECUTIVO, autoSkip: false, font: { size: 11 } } },
      },
      plugins: {
        ...comunes.plugins,
        legend: leyenda,
        // RN8: la cifra al final de cada barra; en las apiladas, dentro de cada tramo.
        datalabels: apiladas
          ? { anchor: 'center', align: 'center', color: COLOR_BLANCO_GRAFICA_SUPER_EJECUTIVO, font: { size: 10, weight: 'bold' }, display: (ctx) => Number(ctx.dataset.data[ctx.dataIndex]) > 0, formatter: (v: number) => cifra(v) }
          : { anchor: 'end', align: 'end', color: COLOR_TEXTO_GRAFICA_SUPER_EJECUTIVO, font: { size: 11 }, formatter: (v: number) => cifra(v) },
      } as ChartOptions['plugins'],
    } as ChartOptions,
  };
}
